import type React from 'react';
import { useEffect, useState } from 'react';

import { DeleteRounded } from '@mui/icons-material';
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  List as MuiList,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { JsonSchemaInput } from '@ragu-components/json-schema-input';
import { componentLogger } from '@ragu-components/logger';
import {
  AudioPlayer,
  OrdBottomToolbar,
  OrdFormList,
  OrdIconStandard,
  OrdTabbedForm,
  useFormType,
} from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { SoundfileUpdateFields, dataSchema } from '@ragu-components/ord-types';
import {
  TabbedForm,
  useDelete,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useResourceContext,
  useTranslate,
} from 'react-admin';

import { Dropzone } from 'src/components/dropzone';
import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';
import { OrdDescriptionInput } from 'src/components/formInputs';
import { uploadFile } from 'src/lib/tus';

import { getErrorMessage } from './utils';

import type { OrdTabbedFormProps } from '@ragu-components/ord-components';
import type { Soundfile } from '@ragu-components/ord-types';

export const SoundfileForm = (props: OrdTabbedFormProps): React.ReactElement => {
  const translate = useTranslate();
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [fileError, setFileError] = useState<string | null>();
  const record = useRecordContext<Soundfile>();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const resource = useResourceContext();
  const recordId = record != null ? record.id : undefined;
  const recordName = record != null ? record.name : '';
  const confirmTitle = `${translate(`resources.soundfiles.operations.delete`)} ${recordName}`;
  const [deleteOne] = useDelete();
  const { create, edit } = useFormType();
  const [isFileLoading, setIsFileLoading] = useState(edit);
  const forceSaveButtonEnabled = edit && selectedFile != null;
  const forceSaveButtonDisabled = create && selectedFile == null;
  const dataProvider = useIngordDataProvider();

  useEffect(() => {
    const fetchAudio = async (): Promise<void> => {
      if (recordId !== undefined) {
        try {
          setIsFileLoading(true);
          const { data, fileType, filename } = await dataProvider.downloadFile({
            resource: 'soundfiles',
            resource_id: recordId,
          });
          const uint8Array = new Uint8Array(data);
          const blob = new Blob([uint8Array], { type: fileType });
          const fileExtension = fileType.split('/')[1];
          const file = new File([blob], filename ?? `${recordName}.${fileExtension}`, { type: fileType });
          if (data != null) {
            setSelectedFile(file);
          }
        } catch (error) {
          componentLogger.error('Error fetching audio file:', error);
        } finally {
          setIsFileLoading(false);
        }
      }
    };
    if (edit) {
      void fetchAudio();
    }
  }, [dataProvider, edit, recordId, recordName]);

  const onFileSelected = (file: File | undefined): void => {
    setSelectedFile(file);
  };

  const onFileRemoved = (): void => {
    setSelectedFile(undefined);
  };

  const onFileRejected = (rejected: boolean): void => {
    setFileError(rejected ? 'resources.soundfiles.errors.fileInvalid' : null);
  };

  const rollback = async (id: string): Promise<void> => {
    if (create) {
      return deleteOne(Resources.soundfiles, { id });
    }
  };

  const onSuccess = async ({ id }: Soundfile): Promise<void> => {
    if (selectedFile != null) {
      await uploadFile(selectedFile, id, Resources.soundfiles)
        .then(async () => {
          const message = `ra.notification.${create ? 'created' : 'updated'}`;
          notify(message, { messageArgs: { smart_count: 1 } });
          redirect('list', resource);
          refresh();
        })
        .catch(async (error: unknown | number) => {
          setFileError(getErrorMessage(error));
          setSelectedFile(undefined);
          await rollback(id);
        });
    }
  };

  const LoadingProgress = (): React.ReactElement => {
    return (
      <Stack width='100%' height='100%' direction={'row'} justifyContent={'center'} alignItems={'center'}>
        <CircularProgress />
      </Stack>
    );
  };

  return (
    <OrdTabbedForm
      sanitizeFields
      replaceRelationships
      createJsonSchema={dataSchema.SoundfileCreate}
      editJsonSchema={dataSchema.SoundfileUpdate}
      toolbar={
        <OrdBottomToolbar
          saveProps={{
            type: 'button',
            mutationOptions: { onSuccess },
            alwaysEnable: forceSaveButtonEnabled,
            disabled: forceSaveButtonDisabled,
          }}
          deleteProps={{ confirmTitle, record: { id: recordId } }}
          strictDelete={false}
        />
      }
      {...props}
    >
      <TabbedForm.Tab label={translate('resources.soundfiles.operations.sections.details')}>
        {isFileLoading ? (
          <LoadingProgress />
        ) : (
          <MuiList sx={{ width: '100%' }}>
            <OrdFormList icon={<OrdIconStandard name='audioFile' />}>
              <JsonSchemaInput source={SoundfileUpdateFields.name} />
              <OrdDescriptionInput source={SoundfileUpdateFields.description} />
              {selectedFile != null ? (
                <Stack direction='row' spacing={1} alignItems='center'>
                  <TextField variant='outlined' defaultValue={selectedFile.name} fullWidth disabled />
                  <InputAdornment position='end'>
                    <IconButton onClick={onFileRemoved}>
                      <DeleteRounded />
                    </IconButton>
                  </InputAdornment>
                </Stack>
              ) : (
                <>
                  <Dropzone
                    accept={{ 'audio/mpeg': ['.mp3'], 'audio/wav': ['.wav'] }}
                    hasError={Boolean(fileError)}
                    onFileSelected={onFileSelected}
                    onFileRejected={onFileRejected}
                  />
                  {fileError != null && (
                    <Typography variant='caption' color='error'>
                      {translate(fileError)}
                    </Typography>
                  )}
                </>
              )}
              {selectedFile != null ? (
                <AudioPlayer
                  title={selectedFile.name}
                  src={URL.createObjectURL(selectedFile)}
                  filename={selectedFile.name}
                />
              ) : (
                <Stack display='none' />
              )}
            </OrdFormList>
          </MuiList>
        )}
      </TabbedForm.Tab>
    </OrdTabbedForm>
  );
};
